import saleApi, { PlanChangePriceOption, SalePlanOption } from "@api/saleApi";
import mathUtils from "@utils/mathUtils";

/** 
 黑卡優惠價計算:對齊BE
group.discount = 17
member_group_discount = self.special_price * (group.discount / 100) => 17% off
return int(Calculator.to_quantize(discount, to_dot=0)) => 4捨5入
 * */

const vipDiscount = 17; // 17% off
/**
 *
 * @param specialPrice 優惠價 unitPrice * groupQty
 * @returns 黑卡會員價
 */
const getDefaultVIPprice = (specialPrice: number) => {
  // specialPrice 優惠價
  // 黑卡價預設價 = 方案優惠價 - (方案優惠價 * 17%)
  const discount = Math.round(specialPrice * (vipDiscount / 100));
  return specialPrice - discount;
};

export interface getExpGrossMarginPayload {
  planId: number;
  newPriceD: Record<string, number>;
  expVipPrice: number;
  startAt?: string;
}

/**
 *
 * @param planId 方案id
 * @param newPriceD special_price 變價後的攤提價格 (因為BE成本計算是總價-discount)
 * @param startAt? 變價開始時間 (YYYY-MM-DD)
 * @returns 毛利率 試算毛利率 (黑卡價 - 成本) / 黑卡價;
 * 方案總成本: 從後端api取得
 */
const getExpGrossMargin = async (payload: getExpGrossMarginPayload) => {
  const errorMessage = "計算成本失敗，請確認方案設定或聯繫管理員";
  const { expVipPrice, ...apiPayload } = payload;
  try {
    const cost = await saleApi.fetchChangePricePlanCost(apiPayload);
    if (!cost || typeof cost !== "number") {
      throw new Error(errorMessage);
    }
    return Math.round(((expVipPrice - cost) / expVipPrice) * 10000) / 100;
  } catch (e: any) {
    return undefined;
  }
};

export type OptionsPriceMap = Map<number, OptionsObj>;
type OptionsObj = { price: number; special: number | null; vip: number | null; priceRatio: number };

export type AllocateDataMap = Map<number, OptionData>;
type OptionData = {
  price: number;
  discountPrice: number;
  priceRatio: number;
};
/**
 *  根據售價分配折扣價格：
 *  option的specialPrice和vipPrice依照選項對售價的比例分配價格(四捨五入至整數),如果產生差額依照下列原則補價：
 *  option售價佔比相等 => 差額就加到第一個(id最小的option)
 *  售價佔比不相等 => 差額加到售價佔比最大的option(priceRatio)
 * @param priceMap 初始價格Map
 * @returns
 */
export const AllocatePriceModal = (priceMap?: AllocateDataMap) => {
  let originPriceMap: AllocateDataMap = priceMap ?? new Map(); // 原始價格Map
  let nowPriceMap: AllocateDataMap = new Map(); // 當前價格Map

  /**
   * 價格處理器
   * getRoundPrice: 根據原始價格佔比計算四捨五入後的價格
   * getSum:options的價格總和
   * @returns {
   * getRoundPrice: (planTotalPrice: number, ratio: number) => number,
   * getSum: () => number
   * }
   */
  const discountPriceHandler = () => {
    let sum = 0;
    return {
      getRoundPrice: (planTotalPrice: number, ratio: number) => {
        const optionPrice = mathUtils.round(planTotalPrice * ratio, 0);
        if (optionPrice) sum += optionPrice;
        return optionPrice;
      },
      getSum: () => sum,
    };
  };
  /**
   * 差額處理器
   * @returns {
   * updateMax: (ratio: number, optionId: number) => void,
   * getDiffOptionId: (mapSize: number) => number,
   */
  const createDifferenceHandler = () => {
    let maxRatio = 0;
    const ids: number[] = [];

    return {
      updateMax: (ratio: number, optionId: number) => {
        if (ratio > maxRatio) {
          maxRatio = ratio;
          ids.unshift(optionId); // 最大的放第一個
        } else if (ratio === maxRatio) {
          ids.push(optionId);
        }
      },
      getDiffOptionId: (mapSize: number) => {
        // 取得要補差額的id
        if (mapSize === ids.length) return ids.sort((a, b) => a - b)[0];
        return ids[0];
      },
    };
  };

  /**
   *
   * @param planPriceData {price:原始價格, discountPrice: 折扣價格}
   * 案例： 優惠價:{price:售價, discountPrice: 優惠價} | 黑卡價: {price:優惠價, discountPrice: 黑卡價}
   * @param inputPriceMap? 有傳入會用傳入的map, 沒傳入會用原始的map
   * @returns {OptionsPriceMap} 分配後的價格Map
   * 1. 計算option的價格
   * 2. 計算option的差額
   * 3. 補差額
   */
  const allocatePriceByOriginalRatio = (
    planPriceData: {
      price: number;
      discountPrice: number;
    },
    inputPriceMap?: AllocateDataMap,
  ) => {
    const optionsPriceMap = inputPriceMap ?? originPriceMap;
    const result: AllocateDataMap = new Map();
    const { discountPrice: planDiscountPrice } = planPriceData;

    const priceCalculator = discountPriceHandler();
    const differenceHandler = createDifferenceHandler();

    //  計算option的價格
    optionsPriceMap.forEach((value, id) => {
      const { priceRatio, price } = value;
      const discountPrice = priceCalculator.getRoundPrice(planDiscountPrice, priceRatio);

      result.set(id, {
        price,
        discountPrice,
        priceRatio,
      });
      differenceHandler.updateMax(priceRatio, id);
    });

    // 計算差額
    const priceDiff = planDiscountPrice - priceCalculator.getSum();

    // 補差額
    if (priceDiff !== 0) {
      const optionId = differenceHandler.getDiffOptionId(optionsPriceMap.size);
      const data = result.get(optionId);

      if (data) {
        result.set(optionId, { ...data, discountPrice: data.discountPrice + priceDiff });
      }
    }
    nowPriceMap = result;
    return result;
  };

  return {
    setOriginPriceMap: (newPriceMap: AllocateDataMap) => {
      originPriceMap = newPriceMap;
    },
    allocatePriceByOriginalRatio,
    nowPriceMap,
    originPriceMap,
  };
};

export enum RadioType {
  DEFAULT = 1,
  TIMING = 2,
}

type PlanFiledValidData = {
  price: number;
  specialPrice: number | undefined;
  periodType: RadioType;
  startAt: string;
  endAt: string;
  vipPrice: number | undefined;
};
type OptionFieldValidData = {
  specialPrice: number | undefined;
  vipPrice: number | undefined;
  isGift: boolean;
};

/**
 * planField:
 * 優惠價必填 且為整數  specialPrice
 * 優惠價必須小於售價 specialPrice < price
 * 黑卡會員價價必填 且為整數 vipPrice
 * 黑卡價必須小於優惠價 vipPrice < specialPrice
 * 選擇時間選項，時間區間必填  radio === RadioType.TIMING && optionDiscountPeriod.length === 0
 */

const changePriceValidator = {
  /**
   * 判斷option是否為贈品: sku包含"GIFT"或productName包含"贈品"
   * 新增SalePlanOption用sku或productName判斷
   * 編輯PlanChangePriceOption用productName判斷(因為BE沒有回傳sku)
   * @param option
   * @returns
   */
  isGift: (option: SalePlanOption | PlanChangePriceOption) => {
    let sku = "";
    let productName = "";

    if ("vpc" in option) {
      //  是 SalePlanOption 類型
      sku = option.vpc.sku;
      productName = option.vpc.productName;
    } else {
      // 是 PlanChangePriceOption 類型
      productName = option.optionName;
    }
    return sku.toUpperCase().includes("GIFT") || productName.includes("贈品");
  },
  planField: (data: PlanFiledValidData) => {
    const response = {
      status: true,
      message: "",
      validData: data,
    };
    const { price, specialPrice, periodType, startAt, endAt, vipPrice } = data;
    if (!specialPrice || !Number.isInteger(specialPrice)) {
      return {
        status: false,
        message: "方案優惠價必填 且為整數",
      };
    }
    if (specialPrice > price) {
      return {
        status: false,
        message: "優惠價必須小於售價",
      };
    }
    if (!vipPrice || !Number.isInteger(vipPrice)) {
      return {
        status: false,
        message: "黑卡會員價必填 且為整數",
      };
    }
    if (vipPrice > specialPrice) {
      return {
        status: false,
        message: "黑卡價必須小於優惠價",
      };
    }
    if (periodType === RadioType.TIMING && (!startAt || !endAt)) {
      return {
        status: false,
        message: "請填入時間區間",
      };
    }
    return response;
  },
  optionField: (data: OptionFieldValidData[]) => {
    const response = {
      status: true,
      message: "",
      validData: data,
    };
    for (const item of data) {
      const { specialPrice, vipPrice, isGift } = item;
      if (!isGift) {
        if (specialPrice !== undefined && specialPrice <= 0) {
          response.status = false;
          response.message = "選項優惠價不得為0, 請調整優惠價";
          break;
        }
        if (vipPrice !== undefined && vipPrice <= 0) {
          response.status = false;
          response.message = "選項黑卡價不得為0, 請調整黑卡價";
          break;
        }
      }
    }
    return response;
  },
};

export default {
  getDefaultVIPprice,
  getExpGrossMargin,
  changePriceValidator,
};
